import clsx from 'clsx'
import { css, html, nothing, unsafeCSS } from 'lit'
import { property, query, state } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'

import { FileError, HttpError } from '@/common/errors'
import { CommonFormElementSize } from '@/common/types'
import { FileListElement } from '@/components/file-input/file-input-list-item.component'
import { GenericFormElement } from '@/internal/mixin/genericFormElement'
import { i18next, t, translate } from '@/localization/localization'

import DapDSButton from '../button/button.component'
import DapDSFeedback from '../feedback/feedback.component'
import DapDSFormLabel from '../form/form-label/form-label.component'
import DapDSIcon from '../icon/icon.component'
import DapDSIconButton from '../icon-button/icon-button.component'
import DapDSModal from '../modal/modal.component'
import DapDSTooltip from '../tooltip/tooltip.component'
import DapDSTypography from '../typography/typography.component'
import style from './file-input.scss?inline'
import DapDSFileInputList from './file-input-list.component'
import DapDSFileInputListItem from './file-input-list-item.component'

/**
 * `dap-ds-file-input`
 * @summary A file input is a control that allows users to select files to upload.
 * @element dap-ds-file-input
 * @title - File input
 *
 * @property {string} name - The name of the file input.
 * @property {string} value - The value of the file input.
 * @property {'success' | 'error'} status - The status of the file input.
 * @property {string} label - The label for the file input.
 * @property {boolean} hideLabel - Visually hides the label while keeping it available to assistive technology. (default: false)
 * @property {string} description - The description for the file input.
 * @property {string} tooltip - The tooltip for the file input.
 * @property {'top' | 'right' | 'bottom' | 'left'} tooltipPlacement - The tooltip placement of the input.
 * @property {boolean} disabled - Whether the file input is disabled.
 * @property { 'xs' | 'sm' | 'lg' } size - The size of the file input.
 * @property {string} feedback - The feedback for the file input.
 * @property {'negative' | 'positive' | 'warning'} feedbackType - The type of feedback for the file input.
 * @property {boolean} required - Whether the file input is required.
 * @property {string} requiredLabel - The required indicator of the file input. (default: '*')
 * @property {boolean} optional - Whether the file input is optional.
 * @property {string} optionalLabel - The optional label of the file input.
 * @property {boolean} subtle - Font weight of the feedback label. Default is `false` which is bold.
 * @property {string} uploadButtonLabel - The label for the upload button.
 * @property {string} browseButtonLabel - The label for the browse button.
 * @property {string} accept - The file types that the file input accepts.
 * @property {boolean} multiple - Whether the file input accepts multiple files.
 * @property {boolean} keepValue - Whether to keep the value when new files are selected.
 * @property {string} uploadUrl - A remote URL to upload files to.
 * @property {string} deleteUrl - A remote URL to delete uploaded files from. Supports placeholders: `{fileName}`, `{id}`, `{key}`, `{location}`.
 * @property {string} uploadHeaders - Custom headers for upload requests (JSON string).
 * @property {string} deleteHeaders - Custom headers for delete requests (JSON string).
 * @property {boolean} autoupload - Whether to automatically upload files after selection.
 * @property {number} maxFiles - The maximum number of files that can be uploaded.
 * @property {number} maxFileSize - The maximum size of files that can be uploaded (in bytes).
 * @property {number} maxSizePerFile - The maximum size a single file can have (in bytes).
 * @property {string} maxFileSizeErrorText - Text for the max file size error.
 * @property {string} maxSizePerFileErrorText - Text for the max size per file error.
 * @property {string} maxFileAmountErrorText - Text for the max file amount error.
 * @property {string} fileTypeErrorText - Text for the file type error.
 * @property {boolean} showDropzone - Whether to show a separate dropzone area.
 * @property {string} dropzoneText - Text to display in the dropzone.
 * @property {string} dropzoneAriaLabel - Aria label for the dropzone.
 * @property {boolean} dropzoneFocusable - Whether the dropzone is in the tab order and exposes button keyboard behavior (default: true).
 * @property {boolean} showFileList - Whether to show the built-in file list.
 * @property {boolean} confirmRemove - Whether to show confirmation dialog before removing files.
 * @property {string} showUploadButton - Whether to show the upload button. Default is `true`.
 * @property {string} removeButtonLabel - The label for the remove button.
 * @property {string} cancelButtonLabel - The label for the cancel button.
 *
 * @event {{ file: File }} dds-upload-start - Fired when a file upload starts.
 * @event {{ file: File, progress: number }} dds-upload-progress - Fired during file upload progress.
 * @event {{ file: File, item: DapDSFileInputListItem, result: any }} dds-upload-complete - Fired when a file upload completes successfully.
 * @event {{ file?: File, errors: FileError[], item?: DapDSFileInputListItem }} dds-upload-error - Fired when a file upload encounters an error. Always contains an errors array, even if there is only one error.
 * @event {{ void }} dds-all-uploads-complete - Fired when all file uploads are complete.
 * @event {{ file: FileListElement }} dds-file-removed - Fired when a file is removed from the file input.
 * @event {{ file: FileListElement, error: Error }} dds-file-delete-error - Fired when a file deletion from server encounters an error.
 * @event {{ newFiles: File[], files: File[], canceledFiles: Set<File>, currentFiles: File[] }} dds-file-change - Fired when the file input value changes. This event is cancelable. Event detail contains: `newFiles` (just the files being added in this operation), `currentFiles` (all files that were already selected before this operation), `files` (all files after merge with existing when keepValue=true), and `canceledFiles` (Set to add files you want to reject). You can either: (1) Call `event.preventDefault()` to reject all files, or (2) Add specific files to `canceledFiles` to reject only those files.
 * @event {{ files: File[] }} dds-files-accepted - Fired when the file input value changes. This event is cancelable. Event detail contains: `files` (all files after merge with existing when keepValue=true).
 *
 * @slot dropzone-content - The content to display in the dropzone.
 *
 * @csspart base - The main file input container.
 * @csspart input - The file input control.
 * @csspart label - The file input label.
 * @csspart description - The file input description.
 * @csspart feedback - The file input feedback.
 * @csspart tooltip - The file input tooltip.
 * @csspart upload-progress - The upload progress container.
 * @csspart browse-button - The browse button.
 * @csspart upload-button - The upload button.
 * @csspart dropzone - The dropzone area.
 *
 * @cssproperty --dds-file-input-holder-margin - The margin of the input holder in shorthand format: top right bottom left (default: var(--dds-spacing-400) 0 0 0)
 * @cssproperty --dds-file-input-dropzone-padding - The padding of the dropzone (default: var(--dds-spacing-800))
 * @cssproperty --dds-file-input-dropzone-border - The border of the dropzone in shorthand format: width style color (default: var(--dds-border-width-base) dashed var(--dds-border-neutral-subtle))
 * @cssproperty --dds-file-input-dropzone-border-radius - The border radius of the dropzone (default: var(--dds-radius-base))
 * @cssproperty --dds-file-input-dropzone-background - The background of the dropzone (default: var(--dds-color-background-primary))
 * @cssproperty --dds-file-input-dropzone-border-hover - The border of the dropzone on hover in shorthand format: width style color (default: var(--dds-border-width-base) dashed var(--dds-color-border-primary-hover))
 * @cssproperty --dds-file-input-dropzone-background-hover - The background of the dropzone on hover (default: var(--dds-color-background-primary-hover))
 * @cssproperty --dds-file-input-dropzone-border-dragover - The border of the dropzone when dragging over in shorthand format: width style color (default: var(--dds-border-width-base) dashed var(--dds-color-border-accent))
 * @cssproperty --dds-file-input-dropzone-background-dragover - The background of the dropzone when dragging over (default: var(--dds-color-background-accent-light))
 * @cssproperty --dds-file-input-dropzone-border-disabled - The border of the disabled dropzone in shorthand format: width style color (default: var(--dds-border-width-base) dashed var(--dds-border-neutral-disabled))
 * @cssproperty --dds-file-input-dropzone-gap - The gap between elements in the dropzone content (default: var(--dds-spacing-300))
 * @cssproperty --dds-file-input-dropzone-accept-color - The text color of the accepted file types in the dropzone (default: var(--dds-color-text-secondary))
 */
export default class DapDSFileInput extends GenericFormElement {
  static tagName = 'dap-ds-file-input'

  static dependencies = {
    'dap-ds-form-label': DapDSFormLabel,
    'dap-ds-tooltip': DapDSTooltip,
    'dap-ds-icon-button': DapDSIconButton,
    'dap-ds-icon': DapDSIcon,
    'dap-ds-typography': DapDSTypography,
    'dap-ds-button': DapDSButton,
    'dap-ds-modal': DapDSModal,
    'dap-ds-feedback': DapDSFeedback,
  }

  @query('#input') private readonly input!: HTMLInputElement

  /** Label for the upload button */
  @property() uploadButtonLabel: string

  /** Label for the browse button */
  @property() browseButtonLabel: string

  /** `data-testid` for the browse button. */
  @property() browseButtonTestId = 'file-input-browse-button'

  /** `data-testid` for the upload button. */
  @property() uploadButtonTestId = 'file-input-upload-button'

  /** Whether to show a separate dropzone area */
  @property({ type: Boolean }) showDropzone = false

  /** Text to display in the dropzone */
  @property() dropzoneText: string

  /** Whether to show the built-in file list */
  @property({ type: Boolean }) showFileList = false

  /** Whether to show confirmation dialog before removing files */
  @property({ type: Boolean }) confirmRemove = true

  /** Whether to show the upload button */
  @property({ type: String }) showUploadButton = 'true'

  /** Whether to show the browse button */
  @property({ type: String }) showBrowseButton = 'true'

  /** The file types that the file input accepts */
  @property() accept: string

  /** Whether the file input accepts multiple files. */
  @property({ type: Boolean }) multiple = false

  /** Whether to keep the value of the file input when new files are selected. */
  @property({ type: Boolean }) keepValue = false

  /** A remote URL to upload files to */
  @property() uploadUrl: string

  /** Whether to automatically upload files after selection */
  @property({ type: Boolean }) autoupload: boolean

  /** The property to use when uploading files */
  @property() uploadProperty = 'file'

  /** The HTTP method to use when uploading files */
  @property() uploadMethod = 'POST'

  /** A remote URL to delete uploaded files from */
  @property() deleteUrl: string

  /** The HTTP method to use when deleting files */
  @property() deleteMethod = 'DELETE'

  /** Whether to send credentials with the file upload request */
  @property({ type: Boolean }) withCredentials = false

  /** Custom headers to send with upload requests (JSON string) */
  @property() uploadHeaders: string

  /** Custom headers to send with delete requests (JSON string) */
  @property() deleteHeaders: string

  /** The file list component to bind to */
  @property({ attribute: false }) fileList: DapDSFileInputList | null = null

  /** The maximum number of files that can be uploaded */
  @property({ type: Number }) maxFiles?: number

  /** The maximum size of files that can be uploaded (in bytes) sum of all files */
  @property({ type: Number }) maxFileSize?: number

  /** The maximum size a single file can have (in bytes) */
  @property({ type: Number }) maxSizePerFile?: number

  /** Text for the max file size error */
  @property({ type: String }) maxFileSizeErrorText?: string

  /** Text for the max size per file error */
  @property({ type: String }) maxSizePerFileErrorText?: string

  /** Text for the max file amount error */
  @property({ type: String }) maxFileAmountErrorText?: string

  /** Text for the file type error */
  @property({ type: String }) fileTypeErrorText?: string

  /** The label for the remove button */
  @property() removeButtonLabel: string

  /** The label for the cancel button */
  @property() cancelButtonLabel: string

  /** Whether the remove button is dangerous */
  @property({ type: String }) removeButtonDanger = 'true'

  /** Whether to show thumbnail preview for supported file types in the built-in file list */
  @property({ type: String }) showThumbnail = 'true'

  /** Aria label for the dropzone */
  @property() dropzoneAriaLabel: string

  /**
   * When false, the dropzone is not tab-focusable (tabindex -1), uses role="group"
   * instead of role="button", and Enter/Space no longer opens the file dialog from
   * the dropzone. Click and drag-and-drop still work. When the browse button is
   * also hidden, slot a focusable control into dropzone-content to keep a keyboard
   * path to the file dialog — the hidden native input is out of the tab order
   * whenever a dropzone is rendered.
   */
  @property({ type: String }) dropzoneFocusable = 'true'

  /** Size of the thumbnail preview in the built-in file list */
  @property({ type: String })
  thumbnailSize: 'xxs' | 'xs' | 'sm' | 'md' | 'lg' = 'sm'

  @property({ type: Array, attribute: false })
  get files() {
    return this._files
  }

  @state() private _files: File[] = []
  @state() private _uploadProgress: Record<string, number> = {}
  @state() private readonly _uploadingFiles: Set<string> = new Set()
  @state() private readonly _activeUploads: Map<string, XMLHttpRequest> =
    new Map()
  @state() private readonly _uploadedFiles: Map<string, any> = new Map() // Track uploaded files with their server response
  @state() private _isDragOver = false
  @state() private _showRemoveDialog = false
  @state() private _fileToRemove: File | null = null
  private _removeResolve: ((value: unknown) => void) | null = null
  private _removeReject: ((reason?: any) => void) | null = null

  static readonly styles = css`
    ${unsafeCSS(style)}
  `

  checkFileEquality(
    file1: File | FileListElement,
    file2: File | FileListElement,
  ) {
    return (
      file1.name === file2.name &&
      file1.size === file2.size &&
      file1.type === file2.type &&
      file1.lastModified === file2.lastModified
    )
  }

  /**
   * Validates if a file type is accepted based on the accept attribute
   */
  private validateFileType(file: File): boolean {
    if (!this.accept) return true

    const acceptedTypes = this.accept.split(',').map(type => type.trim())

    return acceptedTypes.some(acceptedType => {
      // Handle MIME types (e.g., "image/jpeg", "text/*")
      if (acceptedType.includes('/')) {
        if (acceptedType.endsWith('/*')) {
          const baseType = acceptedType.slice(0, -2)
          return file.type.startsWith(baseType)
        }
        return file.type === acceptedType
      }

      // Handle file extensions (e.g., ".jpg", ".pdf")
      if (acceptedType.startsWith('.')) {
        return file.name.toLowerCase().endsWith(acceptedType.toLowerCase())
      }

      return false
    })
  }

  public get focusElement() {
    return this.input
  }
  /**
   * Validates a file list against all constraints (count, size, type)
   */
  private validateFiles(fileList: FileList): {
    isValid: boolean
    errors?: Array<{
      file?: File
      message: string
      type: string
      meta?: Record<string, any>
    }>
  } {
    const errors: Array<{
      file?: File
      message: string
      type: string
      meta?: Record<string, any>
    }> = []

    // Check file count
    if (this.maxFiles && fileList.length + this._files.length > this.maxFiles) {
      for (const file of fileList) {
        errors.push({
          file: file,
          meta: {
            maxFiles: this.maxFiles,
            totalFiles: fileList.length + this._files.length,
          },
          message:
            this.maxFileAmountErrorText ||
            t('fileInput.error.fileAmount', {
              count: this.maxFiles,
            }),
          type: 'maxFiles',
        })
      }
    }

    // Check file types and sizes
    let totalSize = 0
    for (const file of fileList) {
      // Validate file type
      if (!this.validateFileType(file)) {
        errors.push({
          file,
          meta: {
            type: this.accept,
            fileType: file.type,
          },
          message:
            this.fileTypeErrorText ||
            t('fileInput.error.fileType', {
              type: this.accept,
            }),
          type: 'fileType',
        })
      }

      if (this.maxSizePerFile && file.size > this.maxSizePerFile) {
        errors.push({
          file,
          meta: {
            maxSizePerFile: this.maxSizePerFile,
            fileSize: file.size,
          },
          message:
            this.maxSizePerFileErrorText ||
            t('fileInput.error.perFileSize', {
              size: Math.round(this.maxSizePerFile / 1024 / 1024),
            }),
          type: 'maxSizePerFile',
        })
      }

      // Validate file size
      totalSize += file.size
      if (this.maxFileSize && totalSize > this.maxFileSize) {
        errors.push({
          file,
          meta: {
            maxFileSize: this.maxFileSize,
            totalSize: totalSize,
            fileSize: file.size,
          },
          message:
            this.maxFileSizeErrorText ||
            t('fileInput.error.fileSize', {
              size: Math.round(this.maxFileSize / 1024 / 1024),
            }),
          type: 'maxFileSize',
        })
      }
    }

    return {
      isValid: errors.length === 0,
      errors: errors.length > 0 ? errors : undefined,
    }
  }

  public removeFile(fileData: FileListElement | File): Promise<void> | void {
    if (this.confirmRemove) {
      return new Promise((resolve, reject) => {
        this._removeResolve = resolve
        this._removeReject = reject
        this._showRemoveDialog = true
        this._fileToRemove = fileData as File
      })
        .then(() => {
          return this.performFileRemoval(fileData)
        })
        .catch(() => {
          // User cancelled - no action needed
        })
    } else {
      return this.performFileRemoval(fileData)
    }
  }

  private async performFileRemoval(fileData: FileListElement | File) {
    const fileName = fileData.name

    // Check if this file was uploaded and needs to be deleted from server
    if (this._uploadedFiles.has(fileName)) {
      try {
        const uploadResult = this._uploadedFiles.get(fileName)
        await this.deleteUploadedFile(fileName, uploadResult)
        this._uploadedFiles.delete(fileName)
      } catch (error) {
        // Emit delete error event but still remove from local list
        this.emit('dds-file-delete-error', { file: fileData, error })
      }
    }

    this._files = this._files.filter(
      (file: File) => !this.checkFileEquality(file, fileData),
    )

    if (this._files.length === 0 && this.required) {
      this.value = undefined
      this.input.value = ''
    }

    this.emit('dds-file-removed', { file: fileData })
    this.emit('dds-file-change', {
      newFiles: [],
      files: this._files,
      canceledFiles: new Set(),
      currentFiles: this._files,
    })
    this.setValidity()
    // Emit event with final accepted files after removal
    this.emit('dds-files-accepted', { files: this._files })
  }

  private languageChangeHandler = () => {
    // Force re-render when language changes to update translated text
    this.requestUpdate()
  }

  connectedCallback() {
    super.connectedCallback()

    // Listen for language changes to update translated text
    i18next.on('languageChanged', this.languageChangeHandler)

    this.addEventListener('dds-file-remove', event => {
      const customEvent = event as CustomEvent
      this.removeFile(customEvent.detail.file)
    })
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    // Clean up language change listener
    i18next.off('languageChanged', this.languageChangeHandler)
  }

  async onConfirmDelete() {
    if (this._removeResolve) {
      this._removeResolve(true)
    }
    this._showRemoveDialog = false
    this._fileToRemove = null
    this._removeResolve = null
    this._removeReject = null
  }

  onCancelDelete() {
    if (this._removeReject) {
      this._removeReject(new Error('User cancelled'))
    }
    this._showRemoveDialog = false
    this._fileToRemove = null
    this._removeResolve = null
    this._removeReject = null
  }

  onCancel() {
    this.emit('dds-cancel')
    this.setValidity()
  }

  onClick() {
    if (this.disabled) return
    this.input.click()
  }

  onDropzoneKeydown(event: KeyboardEvent) {
    if (this.disabled || this.dropzoneFocusable === 'false') return
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      this.input.click()
    }
  }

  setFileLoadingState(file: File, loading: boolean) {
    if (!this.fileList) {
      return
    }

    const fileComponent = this.fileList.getFileByName(
      file.name,
    )! as DapDSFileInputListItem

    fileComponent.loading = loading

    return fileComponent
  }

  /**
   * Aborts upload for a specific file
   */
  abortUpload(fileName: string) {
    const xhr = this._activeUploads.get(fileName)
    if (xhr) {
      xhr.abort()
      this._uploadingFiles.delete(fileName)
      this._activeUploads.delete(fileName)
      const newProgress = { ...this._uploadProgress }
      delete newProgress[fileName]
      this._uploadProgress = newProgress
    }
  }

  /**
   * Aborts all active uploads
   */
  abortAllUploads() {
    this._activeUploads.forEach(xhr => {
      xhr.abort()
    })
    this._uploadingFiles.clear()
    this._activeUploads.clear()
    this._uploadProgress = {}
  }

  /**
   * Manually start upload of all selected files
   */
  startUpload() {
    if (this._files.length > 0 && this.uploadUrl) {
      this.uploadFiles(this._files)
    }
  }

  /**
   * Delete an uploaded file from the server
   */
  private async deleteUploadedFile(
    fileName: string,
    uploadResult: any,
  ): Promise<void> {
    if (!this.deleteUrl) {
      console.error('Delete URL is not configured')
      return
    }

    const xhr = new XMLHttpRequest()
    xhr.withCredentials = this.withCredentials

    return new Promise((resolve, reject) => {
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve()
        } else {
          reject(
            new HttpError(
              `HTTP error! status: ${xhr.status}`,
              xhr.response,
              xhr.status,
            ),
          )
        }
      }
      xhr.onerror = () => {
        reject(new Error('Network error during file deletion'))
      }

      // Construct delete URL with multiple placeholder support
      let deleteEndpoint = this.deleteUrl

      // Replace common placeholders
      deleteEndpoint = deleteEndpoint.replace(
        '{fileName}',
        encodeURIComponent(fileName),
      )
      deleteEndpoint = deleteEndpoint.replace(
        '{filename}',
        encodeURIComponent(fileName),
      )

      // If uploadResult contains an ID or key, use that for deletion
      if (uploadResult && typeof uploadResult === 'object') {
        if (uploadResult.id) {
          deleteEndpoint = deleteEndpoint.replace(
            '{id}',
            encodeURIComponent(uploadResult.id),
          )
        }
        if (uploadResult.key) {
          deleteEndpoint = deleteEndpoint.replace(
            '{key}',
            encodeURIComponent(uploadResult.key),
          )
        }
        if (uploadResult.location) {
          deleteEndpoint = deleteEndpoint.replace(
            '{location}',
            encodeURIComponent(uploadResult.location),
          )
        }
      }

      xhr.open(this.deleteMethod, deleteEndpoint)

      // Add custom headers for delete
      if (this.deleteHeaders) {
        try {
          const headers = JSON.parse(this.deleteHeaders)
          Object.entries(headers).forEach(([key, value]) => {
            xhr.setRequestHeader(key, value as string)
          })
        } catch (error) {
          console.warn('Invalid deleteHeaders JSON:', this.deleteHeaders)
        }
      }

      // For S3 and most APIs, send minimal data for DELETE requests
      if (this.deleteMethod === 'DELETE') {
        // Some S3-compatible APIs might need the upload result for context
        if (uploadResult && (uploadResult.ETag || uploadResult.VersionId)) {
          xhr.setRequestHeader('Content-Type', 'application/json')
          xhr.send(
            JSON.stringify({
              ETag: uploadResult.ETag,
              VersionId: uploadResult.VersionId,
            }),
          )
        } else {
          xhr.send()
        }
      } else {
        // For other HTTP methods, send the full upload result
        xhr.setRequestHeader('Content-Type', 'application/json')
        xhr.send(JSON.stringify(uploadResult))
      }
    })
  }

  private async uploadFiles(files: File[]) {
    if (!this.uploadUrl) {
      console.error('Upload URL is not configured')
      return
    }

    const totalFiles = files.length
    let completedUploads = 0

    for (const file of files) {
      const formData = new FormData()
      formData.append(this.uploadProperty, file)

      try {
        let fileComponent: DapDSFileInputListItem | undefined = undefined

        // Track upload start
        this._uploadingFiles.add(file.name)
        this._uploadProgress = { ...this._uploadProgress, [file.name]: 0 }

        this.emit('dds-upload-start', { file })

        const xhr = new XMLHttpRequest()
        xhr.withCredentials = this.withCredentials

        // Store active upload for potential abortion
        this._activeUploads.set(file.name, xhr)

        xhr.open(this.uploadMethod, this.uploadUrl)

        // Add custom headers for upload
        if (this.uploadHeaders) {
          try {
            const headers = JSON.parse(this.uploadHeaders)
            Object.entries(headers).forEach(([key, value]) => {
              xhr.setRequestHeader(key, value as string)
            })
          } catch (error) {
            console.warn('Invalid uploadHeaders JSON:', this.uploadHeaders)
          }
        }

        xhr.upload.onprogress = event => {
          if (event.lengthComputable) {
            const progress = (event.loaded / event.total) * 100
            this._uploadProgress = {
              ...this._uploadProgress,
              [file.name]: progress,
            }

            if (this.fileList) {
              fileComponent = this.setFileLoadingState(file, true)
            }

            this.emit('dds-upload-progress', {
              file,
              item: fileComponent,
              progress,
            })
          }
        }

        const response = await new Promise((resolve, reject) => {
          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve(xhr.response)
            } else {
              const error = new HttpError(
                `HTTP error! status: ${xhr.status}`,
                xhr.response,
                xhr.status,
              )
              reject(error)
            }
          }
          xhr.onerror = () => {
            if (this.fileList) {
              fileComponent = this.setFileLoadingState(file, false)
            }

            reject(new Error('Network error'))
          }
          xhr.send(formData)
        })

        const result = JSON.parse(response as string)

        if (this.fileList) {
          fileComponent = this.setFileLoadingState(file, false)
        }

        // Remove from uploading tracking
        this._uploadingFiles.delete(file.name)
        this._activeUploads.delete(file.name)
        const newProgress = { ...this._uploadProgress }
        delete newProgress[file.name]
        this._uploadProgress = newProgress

        // Track uploaded file with its server response
        this._uploadedFiles.set(file.name, result)

        this.emit('dds-upload-complete', { file, item: fileComponent, result })

        completedUploads++
        if (completedUploads === totalFiles) {
          this.emit('dds-all-uploads-complete')
        }
      } catch (error) {
        // Remove from uploading tracking on error
        this._uploadingFiles.delete(file.name)
        this._activeUploads.delete(file.name)
        const newProgress = { ...this._uploadProgress }
        delete newProgress[file.name]
        this._uploadProgress = newProgress

        // Convert error to FileError if it's not already one
        const fileError =
          error instanceof FileError
            ? error
            : new FileError(
                error instanceof Error ? error.message : String(error),
                file,
                error instanceof HttpError ? 'http' : 'upload',
                error instanceof HttpError
                  ? { status: error.status, response: error.response }
                  : undefined,
              )

        if (this.fileList) {
          const fileComponent = this.setFileLoadingState(file, false)
          this.emit('dds-upload-error', {
            file,
            item: fileComponent,
            errors: [fileError],
          })
        } else {
          this.emit('dds-upload-error', { file, errors: [fileError] })
        }
      }
    }
  }

  /**
   * Calculates what the new file list would be if the given files were added
   * Does not modify component state
   */
  private calculateNewFileList(fileList: FileList | null): File[] {
    if (!fileList || fileList.length === 0) {
      return []
    }

    const files = [...this._files]

    if (this.keepValue && this._files?.length) {
      Object.values(fileList).forEach((file: File) => {
        const isFileInList = !!this._files.find((f: File) =>
          this.checkFileEquality(f, file),
        )

        if (!isFileInList) {
          files.push(file)
        }
      })

      return files
    } else {
      return Object.values(fileList)
    }
  }

  private handleFileList(fileList: FileList | null) {
    const files = [...this._files]

    if (this.keepValue && this._files?.length) {
      Object.values(fileList!).forEach((file: File) => {
        const isFileInList = !!this._files.find((f: File) =>
          this.checkFileEquality(f, file),
        )

        if (!isFileInList) {
          files.push(file)
        }
      })

      this._files = files
    } else {
      this._files = fileList ? Object.values(fileList) : []
    }

    const dataTransfer = new DataTransfer()

    this._files.forEach(file => {
      dataTransfer.items.add(file)
    })
    this.input.files = dataTransfer.files

    if (this.autoupload) {
      this.uploadFiles(this._files)
    }
  }

  override onChange(event: InputEvent) {
    const target = event?.target as HTMLInputElement
    const targetFiles = target.files

    // If no files were selected (user canceled the dialog) and we have existing files,
    // just reset the input value and return early without processing or emitting events
    if ((!targetFiles || targetFiles.length === 0) && this._files.length > 0) {
      // Reset the input value to allow selecting the same file again if needed
      this.input.value = ''
      return
    }

    if (targetFiles) {
      const validation = this.validateFiles(targetFiles)
      if (!validation.isValid && validation.errors) {
        // Collect all validation errors into an array
        // All validation errors should have a file, but ensure we always create FileError
        const errors = validation.errors.map(error => {
          if (error.file) {
            return new FileError(
              error.message,
              error.file,
              error.type,
              error.meta,
            )
          } else {
            // Fallback: create a FileError with the first file if available
            // This should not happen in practice, but ensures type safety
            const fallbackFile =
              targetFiles.length > 0 ? targetFiles[0] : new File([], 'unknown')
            return new FileError(
              error.message,
              fallbackFile,
              error.type,
              error.meta,
            )
          }
        })

        // Emit all errors as a single array
        this.emit('dds-upload-error', { errors })
        return
      }
    }

    // Get the newly added files from the input
    const newFiles = targetFiles ? Array.from(targetFiles) : []

    // Calculate what the merged file list would be (existing + new)
    const allFiles = this.calculateNewFileList(targetFiles)

    // Create a Set for files that should be canceled
    const canceledFiles = new Set<File>()

    // Emit cancelable event with both new files and all files
    const changeEvent = this.emit(
      'dds-file-change',
      { newFiles, files: allFiles, canceledFiles, currentFiles: this._files },
      { cancelable: true },
    )

    // If the event was prevented, reset the input and return
    if (changeEvent.defaultPrevented) {
      this.input.value = ''
      return
    }

    // Filter out canceled files from targetFiles
    if (canceledFiles.size > 0 && targetFiles) {
      const acceptedFiles = Array.from(targetFiles).filter(
        file => !canceledFiles.has(file),
      )

      // If all files were canceled, reset the input
      // if (acceptedFiles.length === 0) {
      //   this.input.value = ''
      //   return
      // }

      // Create a new FileList with only accepted files
      const dataTransfer = new DataTransfer()
      acceptedFiles.forEach(file => dataTransfer.items.add(file))

      this.value = this.input.value
      this.handleFileList(dataTransfer.files)
      this.setValidity()
      // Emit event with final accepted files after processing canceled files
      this.emit('dds-files-accepted', { files: this._files })
    } else {
      // No files were canceled, apply all changes
      this.value = this.input.value
      this.handleFileList(targetFiles)
      this.setValidity()
      // Emit event with final accepted files
      this.emit('dds-files-accepted', { files: this._files })
    }
  }

  onDragenter(event: DragEvent) {
    if (this.disabled) return
    event.preventDefault()
    this._isDragOver = true
  }

  onDragover(event: DragEvent) {
    if (this.disabled) return
    event.preventDefault()
  }

  onDragleave(event: DragEvent) {
    if (this.disabled) return
    event.preventDefault()

    if (!this.contains(event.relatedTarget as Node)) {
      this._isDragOver = false
    }
  }

  onDrop(event: DragEvent) {
    if (this.disabled) return
    event.preventDefault()
    this._isDragOver = false

    const droppedFiles = event.dataTransfer?.files
    if (droppedFiles) {
      const validation = this.validateFiles(droppedFiles)
      if (!validation.isValid && validation.errors) {
        // Collect all validation errors into an array
        // All validation errors should have a file, but ensure we always create FileError
        const errors = validation.errors.map(error => {
          if (error.file) {
            return new FileError(
              error.message,
              error.file,
              error.type,
              error.meta,
            )
          } else {
            // Fallback: create a FileError with the first file if available
            // This should not happen in practice, but ensures type safety
            const fallbackFile =
              droppedFiles.length > 0
                ? droppedFiles[0]
                : new File([], 'unknown')
            return new FileError(
              error.message,
              fallbackFile,
              error.type,
              error.meta,
            )
          }
        })

        // Emit all errors as a single array
        this.emit('dds-upload-error', { errors })
        return
      }
    }

    // Get the newly dropped files
    const newFiles = droppedFiles ? Array.from(droppedFiles) : []

    // Calculate what the merged file list would be (existing + new)
    const allFiles = this.calculateNewFileList(droppedFiles ?? null)

    // Create a Set for files that should be canceled
    const canceledFiles = new Set<File>()

    // Emit cancelable event with both new files and all files
    const changeEvent = this.emit(
      'dds-file-change',
      { newFiles, files: allFiles, canceledFiles, currentFiles: this._files },
      { cancelable: true },
    )

    // If the event was prevented, return early without modifying state
    if (changeEvent.defaultPrevented) {
      return
    }

    // Filter out canceled files from droppedFiles
    if (canceledFiles.size > 0 && droppedFiles) {
      const acceptedFiles = Array.from(droppedFiles).filter(
        file => !canceledFiles.has(file),
      )

      // If all files were canceled, return early
      // if (acceptedFiles.length === 0) {
      //   return
      // }

      // Create a new FileList with only accepted files
      const dataTransfer = new DataTransfer()
      acceptedFiles.forEach(file => dataTransfer.items.add(file))

      this.handleFileList(dataTransfer.files)
      this.setValidity()
      // Emit event with final accepted files after processing canceled files
      this.emit('dds-files-accepted', { files: this._files })
    } else {
      // No files were canceled, apply all changes
      this.handleFileList(droppedFiles ?? null)
      this.setValidity()
      // Emit event with final accepted files
      this.emit('dds-files-accepted', { files: this._files })
    }
  }

  render() {
    // The native input is the real keyboard control: the browse button is only
    // a tabindex="-1" visual proxy for it. Without that proxy, the visually
    // hidden input would be an invisible tab stop, so it stays in the tab
    // order only while no dropzone is rendered. A rendered dropzone provides
    // the keyboard path instead: Enter/Space when focusable, or a focusable
    // control slotted into dropzone-content when dropzoneFocusable is false.
    const showBrowseButton =
      this.showBrowseButton === 'true' && !this.autoupload
    const inputTabbable = showBrowseButton || !this.showDropzone
    return html`
      <div class="label-container">
        ${this.label &&
        html`<dap-ds-form-label
          part="label"
          id="label"
          for="input"
          .size=${this.size}
          .required=${this.required}
          .requiredLabel=${this.requiredLabel}
          .optional=${this.optional}
          .optionalLabel=${this.optionalLabel}
          .disabled=${this.disabled}
          class=${clsx('label')}
          .noMargin=${!!this.description}
          .hideLabel=${this.hideLabel}
          label=${ifDefined(this.label)}>
        </dap-ds-form-label>`}
        ${this.tooltip &&
        html` <dap-ds-tooltip
          part="tooltip"
          content=${this.tooltip}
          mode="toggle"
          floatingStrategy="fixed"
          placement=${this.tooltipPlacement}>
          <dap-ds-icon-button
            clean
            class="info-button"
            slot="trigger"
            aria-label=${this.tooltipAriaLabel}
            size=${this.size as 'xs' | 'sm' | 'md' | 'lg'}
            iconSize=${this.size === 'xs' ? '16' : '22'}>
            <dap-ds-icon
              name="information-line"
              class="info-button"></dap-ds-icon>
          </dap-ds-icon-button>
        </dap-ds-tooltip>`}
      </div>
      ${this.description &&
      html`
        <dap-ds-typography
          part="description"
          variant="description"
          class=${clsx('input-description')}
          size=${ifDefined(this.size as CommonFormElementSize)}
          id="description">
          ${this.description}
        </dap-ds-typography>
      `}
      ${this.showDropzone
        ? html`
            <div
              class=${clsx(
                'dropzone',
                { 'dropzone--dragover': this._isDragOver },
                { 'dropzone--disabled': this.disabled },
              )}
              part="dropzone"
              @dragenter=${this.onDragenter}
              @dragover=${this.onDragover}
              @dragleave=${this.onDragleave}
              @drop=${this.onDrop}
              @keydown=${this.dropzoneFocusable === 'true'
                ? this.onDropzoneKeydown
                : nothing}
              @click=${this.onClick}
              role=${this.dropzoneFocusable === 'true' ? 'button' : 'group'}
              aria-label=${ifDefined(this.dropzoneAriaLabel)}
              tabindex=${this.dropzoneFocusable === 'true' ? '0' : '-1'}>
              <slot name="dropzone-content">
                <div class="dropzone__content">
                  <dap-ds-icon name="upload-2-line" size="lg"></dap-ds-icon>
                  <dap-ds-typography variant="caption">
                    ${this.dropzoneText || translate('fileInput.dropzone.text')}
                  </dap-ds-typography>
                  ${this.accept &&
                  html`
                    <dap-ds-typography
                      variant="caption"
                      class="dropzone__accept">
                      ${translate('fileInput.dropzone.acceptedTypes')}:
                      ${this.accept}
                    </dap-ds-typography>
                  `}
                </div>
              </slot>
            </div>
          `
        : nothing}
      <span
        part="base"
        class=${clsx(
          'input',
          { 'input--success': this.status === 'success' },
          { 'input--error': this.status === 'error' },
          { 'input--disabled': this.disabled },
        )}
        role="region"
        aria-label="File upload area">
        <div class="input__holder" aria-describedby="drop-instructions">
          <input
            id="input"
            class="input__control sr-only"
            part="input"
            name=${this.name}
            type="file"
            accept=${ifDefined(this.accept)}
            ?disabled=${this.disabled}
            ?multiple=${this.multiple}
            ?required=${this.required}
            tabindex=${inputTabbable ? '0' : '-1'}
            aria-labelledby="label"
            aria-hidden=${inputTabbable ? 'false' : 'true'}
            aria-describedby="description ${this.feedback
              ? 'feedback'
              : nothing}"
            @change=${this.onChange}
            @cancel=${this.onCancel}
            @invalid=${this.handleInvalid}
            @blur=${this.onBlur}
            @focus=${this.onFocus} />
          ${showBrowseButton
            ? html` <dap-ds-button
                class="input__button"
                part="browse-button"
                data-testid=${this.browseButtonTestId}
                tabindex="-1"
                variant="outline"
                ?disabled=${this.disabled}
                size=${ifDefined(this.size) as CommonFormElementSize}
                @click=${this.onClick}
                @dragenter=${this.showDropzone ? nothing : this.onDragenter}
                @dragover=${this.showDropzone ? nothing : this.onDragover}
                @drop=${this.showDropzone ? nothing : this.onDrop}
                aria-label="${this.browseButtonLabel ||
                translate('fileInput.browse')}${this.showDropzone
                  ? ''
                  : ` - ${translate('fileInput.dragDropArea')}`}"
                role="button"
                >${this.browseButtonLabel ||
                translate('fileInput.browse')}</dap-ds-button
              >`
            : nothing}
          ${this.showUploadButton === 'true' && !this.autoupload
            ? html`
                <dap-ds-button
                  class="upload-button"
                  part="upload-button"
                  data-testid=${this.uploadButtonTestId}
                  variant="primary"
                  size=${ifDefined(this.size) as CommonFormElementSize}
                  ?disabled=${this._files.length === 0 ||
                  !this.uploadUrl ||
                  this._uploadingFiles.size > 0}
                  @click=${this.startUpload}
                  >${this.uploadButtonLabel ||
                  translate('fileInput.upload')}</dap-ds-button
                >
              `
            : nothing}
          <div id="drop-instructions" class="sr-only">
            ${translate('fileInput.dragDropInstructions')}
          </div>
        </div>
      </span>
      ${!this.fileList && this._uploadingFiles.size > 0
        ? html`
            <div class="upload-progress" part="upload-progress">
              ${Array.from(this._uploadingFiles).map(fileName => {
                const progress = this._uploadProgress[fileName] || 0
                return html`
                  <div class="upload-progress__item">
                    <dap-ds-typography variant="caption">
                      ${fileName} - ${Math.round(progress)}%
                    </dap-ds-typography>
                    <div class="upload-progress__bar">
                      <div
                        class="upload-progress__fill"
                        style="width: ${progress}%"></div>
                    </div>
                  </div>
                `
              })}
            </div>
          `
        : nothing}
      <dap-ds-modal
        .open=${this._showRemoveDialog}
        title="${translate('fileInput.removeDialog.title')}"
        okButtonLabel=${this.removeButtonLabel ||
        translate('fileInput.removeDialog.confirm')}
        cancelButtonLabel=${this.cancelButtonLabel ||
        translate('fileInput.removeDialog.cancel')}
        .okButtonDanger=${this.removeButtonDanger === 'true'}
        @dds-cancel=${this.onCancelDelete}
        @dds-close=${this.onCancelDelete}
        @dds-ok=${this.onConfirmDelete}>
        <div>
          <dap-ds-typography variant="body">
            ${translate('fileInput.removeDialog.message', {
              fileName: this._fileToRemove?.name ?? '',
            })}
          </dap-ds-typography>
        </div>
      </dap-ds-modal>

      ${this.feedback
        ? html`
            <dap-ds-feedback
              part="feedback"
              feedbackType=${ifDefined(this.feedbackType)}
              feedbackSize=${ifDefined(this.size) as 'lg' | 'sm' | 'xs'}
              id="feedback"
              .feedbackSubtle=${this.subtle}>
              ${this.feedback}
            </dap-ds-feedback>
          `
        : nothing}
    `
  }
}
